import DataTable from "datatables.net-dt";
import "datatables.net-dt/css/dataTables.dataTables.css";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { API_URL } from "./config.js";
import { getToken, requireAuth } from "./auth.js";

document.addEventListener("DOMContentLoaded", () => {
  if (!requireAuth()) return;

  const modal = document.getElementById("carrier-tracking-modal");
  const statusModal = document.getElementById("carrier-status-modal");
  const closeBtn = document.getElementById("carrier-tracking-close-btn");
  const exportBtn = document.getElementById("carrier-tracking-export-btn");
  const statusCloseBtn = document.getElementById("carrier-status-close-btn");
  const openStatusBtn = document.getElementById("tracking-open-status-btn");
  const dateRangeInput = document.getElementById("carrier-tracking-date-range");
  const presetsContainer = document.getElementById("carrier-tracking-presets");
  const statusFiltersContainer = document.getElementById(
    "carrier-tracking-status-filters",
  );
  const modalTitle = document.getElementById("carrier-tracking-modal-title");
  const table = document.getElementById("carrier-tracking-table");
  const tbody = table?.querySelector("tbody");
  const statusForm = document.getElementById("carrier-status-form");
  const statusList = document.getElementById("carrier-status-list");

  let currentCarrier = "CANPAR";
  let currentStatuses = [];
  let currentRows = [];
  let dataTable = null;
  let currentRangePreset = "current-year";
  let currentStatusFilter = "all";
  let rangePicker = null;
  let isApplyingPreset = false;
  let lastLoadRequestId = 0;

  function getNoneStatus() {
    return currentStatuses.find((status) => status.name === "None") || null;
  }

  function getActiveStatuses() {
    return currentStatuses.filter((status) => Number(status.is_active) === 1);
  }

  function getRowStatusId(row) {
    return Number(row.status_id || getNoneStatus()?.id || 0);
  }

  function getRowStatusName(row) {
    return row.status_name || getNoneStatus()?.name || "None";
  }

  function getVisibleStatusFilters() {
    const rowsByStatusId = new Set(
      currentRows.map((row) => getRowStatusId(row)),
    );
    return currentStatuses.filter((status) => {
      const statusId = Number(status.id);
      return Number(status.is_active) === 1 || rowsByStatusId.has(statusId);
    });
  }

  function setActiveStatusFilter(filterValue) {
    currentStatusFilter = filterValue;
    statusFiltersContainer
      ?.querySelectorAll("[data-status-filter]")
      .forEach((button) => {
        const isActive = button.dataset.statusFilter === String(filterValue);
        button.classList.toggle("carrier-status-filter-active", isActive);
      });
  }

  function getFilteredRows() {
    if (currentStatusFilter === "all") {
      return currentRows;
    }

    return currentRows.filter(
      (row) => String(getRowStatusId(row)) === String(currentStatusFilter),
    );
  }

  function toSortableTimestamp(value) {
    if (!value) return null;

    if (value instanceof Date) {
      const ts = value.getTime();
      return Number.isNaN(ts) ? null : ts;
    }

    const raw = String(value).trim();
    if (!raw) return null;

    // MySQL DATETIME format: yyyy-mm-dd hh:mm:ss
    const mysqlMatch = raw.match(
      /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/,
    );
    if (mysqlMatch) {
      const [, y, m, d, hh = "00", mm = "00", ss = "00"] = mysqlMatch;
      const date = new Date(
        Number(y),
        Number(m) - 1,
        Number(d),
        Number(hh),
        Number(mm),
        Number(ss),
      );
      const ts = date.getTime();
      return Number.isNaN(ts) ? null : ts;
    }

    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : date.getTime();
  }

  function sortRowsByOrderDateDesc(rows) {
    return [...rows].sort((left, right) => {
      const leftTs = toSortableTimestamp(left.order_date);
      const rightTs = toSortableTimestamp(right.order_date);

      if (leftTs === null && rightTs === null) {
        return Number(right.id || 0) - Number(left.id || 0);
      }
      if (leftTs === null) return 1;
      if (rightTs === null) return -1;
      if (rightTs !== leftTs) return rightTs - leftTs;

      return Number(right.id || 0) - Number(left.id || 0);
    });
  }

  function getCurrentStatusFilterValue() {
    return currentStatusFilter === "all" ? null : currentStatusFilter;
  }

  function buildQueryParams(includeStatusFilter = false) {
    const params = new URLSearchParams({ carrier: currentCarrier });
    const { from, to } = getSelectedRange();
    if (from) params.set("from", from);
    if (to) params.set("to", to);

    const statusId = includeStatusFilter ? getCurrentStatusFilterValue() : null;
    if (statusId) {
      params.set("statusId", statusId);
    }

    return params;
  }

  function renderStatusFilters() {
    if (!statusFiltersContainer) return;

    const visibleStatuses = getVisibleStatusFilters();
    const allCount = currentRows.length;

    statusFiltersContainer.innerHTML = [
      `
        <button
          type="button"
          data-status-filter="all"
          class="carrier-status-filter px-3 py-1.5 rounded-full border border-gray-300 bg-white text-xs font-semibold text-gray-700 transition hover:bg-gray-100"
        >
          Todos <span class="text-gray-400">${allCount}</span>
        </button>
      `,
      ...visibleStatuses.map((status) => {
        const count = currentRows.filter(
          (row) => Number(getRowStatusId(row)) === Number(status.id),
        ).length;

        return `
          <button
            type="button"
            data-status-filter="${status.id}"
            class="carrier-status-filter px-3 py-1.5 rounded-full border border-gray-300 bg-white text-xs font-semibold text-gray-700 transition hover:bg-gray-100"
            title="${escapeHtml(status.name)}"
          >
            <span class="inline-flex items-center gap-2">
              <span class="h-2 w-2 rounded-full" style="background:${escapeHtml(normalizeColor(status.color))}"></span>
              <span>${escapeHtml(status.name)}</span>
              <span class="text-gray-400">${count}</span>
            </span>
          </button>
        `;
      }),
    ].join("");

    const filterExists =
      currentStatusFilter === "all" ||
      visibleStatuses.some(
        (status) => String(status.id) === String(currentStatusFilter),
      );

    setActiveStatusFilter(filterExists ? currentStatusFilter : "all");
  }

  function syncBodyScrollLock() {
    const hasOpenModal = [modal, statusModal].some(
      (dialog) => dialog && !dialog.classList.contains("hidden"),
    );

    document.body.classList.toggle("modal-open", hasOpenModal);
  }

  function openModal(dialog) {
    if (!dialog) return;
    dialog.classList.remove("hidden");
    dialog.classList.add("flex");
    syncBodyScrollLock();
  }

  function closeModal(dialog) {
    if (!dialog) return;
    dialog.classList.add("hidden");
    dialog.classList.remove("flex");
    syncBodyScrollLock();
  }

  async function reloadRowsSilently() {
    try {
      await loadRows();
    } catch (error) {
      window.showSnackbar?.(
        error.message || "No se pudieron cargar los registros.",
        "error",
      );
    }
  }

  const pad = (value) => String(value).padStart(2, "0");
  const toDateOnly = (value) =>
    `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;

  function startOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }

  function endOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
  }

  function startOfYear(date) {
    return new Date(date.getFullYear(), 0, 1);
  }

  function endOfYear(date) {
    return new Date(date.getFullYear(), 11, 31);
  }

  function getPresetRange(preset) {
    const now = new Date();

    switch (preset) {
      case "current-month":
        return { from: startOfMonth(now), to: endOfMonth(now) };
      case "previous-month": {
        const previousMonth = new Date(
          now.getFullYear(),
          now.getMonth() - 1,
          1,
        );
        return {
          from: startOfMonth(previousMonth),
          to: endOfMonth(previousMonth),
        };
      }
      case "two-months-ago": {
        const targetMonth = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        return { from: startOfMonth(targetMonth), to: endOfMonth(targetMonth) };
      }
      case "all-time":
        return { from: null, to: null };
      case "current-year":
      default:
        return { from: startOfYear(now), to: endOfYear(now) };
    }
  }

  function setActivePreset(preset) {
    currentRangePreset = preset;
    presetsContainer
      ?.querySelectorAll("[data-range-preset]")
      .forEach((button) => {
        const isActive = button.dataset.rangePreset === preset;
        button.classList.toggle("carrier-range-preset-active", isActive);
      });
  }

  function applyPreset(preset) {
    const range = getPresetRange(preset);
    setActivePreset(preset);

    if (!rangePicker) return;

    if (!range.from || !range.to) {
      isApplyingPreset = true;
      rangePicker.clear();
      isApplyingPreset = false;
      return;
    }

    isApplyingPreset = true;
    rangePicker.setDate([range.from, range.to], true);
    isApplyingPreset = false;
  }

  function getSelectedRange() {
    const selectedDates = rangePicker?.selectedDates || [];
    if (selectedDates.length < 2) {
      return { from: null, to: null };
    }

    return {
      from: toDateOnly(selectedDates[0]),
      to: toDateOnly(selectedDates[1]),
    };
  }

  if (dateRangeInput) {
    rangePicker = flatpickr(dateRangeInput, {
      mode: "range",
      dateFormat: "Y-m-d",
      altInput: true,
      altFormat: "d/m/Y",
      locale: {
        rangeSeparator: " a ",
      },
      onChange(selectedDates) {
        if (isApplyingPreset) {
          return;
        }

        if (selectedDates.length === 2) {
          setActivePreset("custom");
          if (!modal?.classList.contains("hidden")) {
            reloadRowsSilently();
          }
        }

        if (
          selectedDates.length === 0 &&
          !modal?.classList.contains("hidden")
        ) {
          reloadRowsSilently();
        }
      },
    });
    applyPreset("current-year");
  }

  const formatDate = (value) => {
    if (!value) return "";
    const timestamp = toSortableTimestamp(value);
    if (timestamp === null) return String(value);

    const date = new Date(timestamp);
    const pad = (segment) => String(segment).padStart(2, "0");
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
  };

  const escapeHtml = (value) =>
    String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const normalizeColor = (color) => {
    if (!color) return "#f3f4f6";
    return /^#[0-9a-f]{6}$/i.test(color) ? color : "#f3f4f6";
  };

  const hexToRgba = (hex, alpha = 0.28) => {
    const normalized = normalizeColor(hex).replace("#", "");
    const red = Number.parseInt(normalized.slice(0, 2), 16);
    const green = Number.parseInt(normalized.slice(2, 4), 16);
    const blue = Number.parseInt(normalized.slice(4, 6), 16);
    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
  };

  async function requestJson(url, options = {}) {
    const token = getToken();
    const headers = {
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || "Error de servidor.");
    }

    return data;
  }

  async function loadStatuses() {
    const params = new URLSearchParams({ carrier: currentCarrier });
    const data = await requestJson(
      `${API_URL}/carrier-tracking/statuses?${params.toString()}`,
    );
    currentStatuses = data.statuses || [];
  }

  function applyRowColor(row, color) {
    row.style.backgroundColor = hexToRgba(color);
  }

  function renderStatusManager(statuses) {
    if (!statusList) return;

    if (!statuses.length) {
      statusList.innerHTML =
        '<div class="rounded-xl border border-dashed border-gray-300 p-6 text-sm text-gray-500">No hay status creados para este carrier.</div>';
      return;
    }

    statusList.innerHTML = statuses
      .map(
        (status) => `
          <div class="rounded-2xl border border-gray-200 bg-gray-50/70 p-4 grid grid-cols-1 md:grid-cols-[1.5fr_140px_120px_auto] gap-4 items-end" data-status-id="${status.id}" data-system-status="${status.name === "None" ? "none" : ""}">
            <label class="block">
              <span class="block text-sm font-semibold text-gray-700 mb-2">Status</span>
              <input data-field="name" type="text" value="${escapeHtml(status.name)}" ${status.name === "None" ? "readonly" : ""} class="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-gray-900 focus:ring-2 focus:ring-gray-100 outline-none text-gray-900 bg-white ${status.name === "None" ? "cursor-not-allowed bg-gray-100 text-gray-500" : ""}" />
            </label>
            <label class="block">
              <span class="block text-sm font-semibold text-gray-700 mb-2">Color</span>
              <input data-field="color" type="color" value="${normalizeColor(status.color)}" class="block w-full h-11 px-2 py-2 rounded-lg border border-gray-200 focus:border-gray-900 focus:ring-2 focus:ring-gray-100 outline-none text-gray-900 bg-white" />
            </label>
            <label class="block">
              <span class="block text-sm font-semibold text-gray-700 mb-2">Orden</span>
              <input data-field="sortOrder" type="number" value="${status.sort_order}" class="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-gray-900 focus:ring-2 focus:ring-gray-100 outline-none text-gray-900 bg-white" />
            </label>
            <div class="flex flex-wrap gap-3 items-center">
              <label class="inline-flex items-center gap-2 text-sm text-gray-700 font-semibold">
                <input data-field="isActive" type="checkbox" ${status.is_active ? "checked" : ""} ${status.name === "None" ? "disabled" : ""} class="accent-black w-4 h-4" />
                Activo
              </label>
              <button type="button" data-action="save-status" class="px-4 py-2 rounded-lg bg-black text-white font-semibold hover:bg-gray-800 transition">Guardar</button>
            </div>
            ${status.name === "None" ? '<div class="md:col-span-4 text-xs font-medium text-gray-500">Status base del sistema. Todos los registros nuevos y los que no tenían status inician en None.</div>' : ""}
          </div>
        `,
      )
      .join("");
  }

  async function loadStatusManager() {
    const data = await requestJson(
      `${API_URL}/carrier-tracking/statuses?carrier=${encodeURIComponent(currentCarrier)}`,
    );
    renderStatusManager(data.statuses || []);
    currentStatuses = data.statuses || [];
  }

  function renderRows(rows) {
    if (!tbody) return;

    const activeStatuses = getActiveStatuses();
    const noneStatus = getNoneStatus();
    const hasActiveStatuses = activeStatuses.length > 0;

    tbody.innerHTML = rows
      .map((row) => {
        const selectedStatusId = row.status_id || noneStatus?.id || "";
        const activeStatusOptions = activeStatuses
          .map(
            (status) => `
              <option value="${status.id}" ${Number(status.id) === Number(selectedStatusId) ? "selected" : ""}>
                ${escapeHtml(status.name)}
              </option>
            `,
          )
          .join("");

        const currentInactiveStatus =
          selectedStatusId &&
          !activeStatuses.some(
            (status) => Number(status.id) === Number(selectedStatusId),
          )
            ? `
              <option value="${selectedStatusId}" selected disabled>
                ${escapeHtml(row.status_name || "None")}
              </option>
            `
            : "";

        const statusOptions = hasActiveStatuses
          ? `${currentInactiveStatus}${activeStatusOptions}`
          : `
              <option value="" selected>
                ${currentStatuses.length ? "No hay status activos" : "Crea un status primero"}
              </option>
              ${currentInactiveStatus}
            `;

        return `
          <tr data-record-id="${row.id}" data-status-color="${escapeHtml(row.status_color || "")}">
            <td>
              <div class="font-semibold text-gray-900">${escapeHtml(row.tracking_number)}</div>
              <div class="mt-1 text-xs text-gray-600">Order #: ${escapeHtml(row.order_number)}</div>
            </td>
            <td>
              <div class="font-semibold text-gray-900">Name: ${escapeHtml(row.customer_name)}</div>
              <div class="mt-1 text-xs text-gray-600">Email: ${escapeHtml(row.customer_email)}</div>
            </td>
            <td>${escapeHtml(formatDate(row.order_date))}</td>
            <td>${escapeHtml(formatDate(row.shipped_date))}</td>
            <td>${escapeHtml(row.delivery_message)}</td>
            <td>
              <select class="carrier-tracking-status-select min-w-[160px] rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" data-record-id="${row.id}" ${hasActiveStatuses ? "" : "disabled"}>
                ${statusOptions}
              </select>
            </td>
          </tr>
        `;
      })
      .join("");

    tbody.querySelectorAll("tr").forEach((row) => {
      applyRowColor(row, row.dataset.statusColor || "");
    });
  }

  async function loadRows() {
    const requestId = ++lastLoadRequestId;
    const params = buildQueryParams(false);

    await loadStatuses();
    const data = await requestJson(
      `${API_URL}/carrier-tracking/records?${params.toString()}`,
    );

    if (requestId !== lastLoadRequestId) {
      return;
    }

    currentRows = data.rows || [];
    renderStatusFilters();
    const filteredRows = sortRowsByOrderDateDesc(getFilteredRows());

    if (dataTable) {
      dataTable.destroy();
      dataTable = null;
    }

    renderRows(filteredRows);

    const activeStatuses = getActiveStatuses();

    dataTable = new DataTable(table, {
      pageLength: 25,
      ordering: false,
      language: {
        search: "Buscar:",
        lengthMenu: "Mostrar _MENU_ registros",
        info: "Mostrando _START_ a _END_ de _TOTAL_ registros",
        infoEmpty: "Sin registros",
        zeroRecords: "No se encontraron registros",
        paginate: {
          previous: "Anterior",
          next: "Siguiente",
        },
      },
    });

    if (!activeStatuses.length) {
      return;
    }
  }

  async function exportCurrentRows() {
    try {
      const token = getToken();
      const response = await fetch(
        `${API_URL}/carrier-tracking/export?${buildQueryParams(true).toString()}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "No se pudo exportar el Excel.");
      }

      const blob = await response.blob();
      const contentDisposition =
        response.headers.get("Content-Disposition") || "";
      const fileNameMatch = contentDisposition.match(/filename="?([^\"]+)"?/i);
      const fileName =
        fileNameMatch?.[1] || `tracking-${currentCarrier.toLowerCase()}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      window.showSnackbar?.(
        error.message || "No se pudo exportar el Excel.",
        "error",
      );
    }
  }

  async function updateStatus(select) {
    if (select.disabled) {
      window.showSnackbar?.(
        "Primero debes crear o activar al menos un status.",
        "error",
      );
      return;
    }

    const noneStatus = getNoneStatus();
    const statusId = Number(select.value || noneStatus?.id || 0);
    const recordId = Number(select.dataset.recordId);

    if (!statusId) {
      window.showSnackbar?.(
        "No se encontró el status None para este carrier.",
        "error",
      );
      return;
    }

    try {
      await requestJson(
        `${API_URL}/carrier-tracking/records/${recordId}/status`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            carrier: currentCarrier,
            statusId,
          }),
        },
      );

      const selectedStatus = currentStatuses.find(
        (status) => Number(status.id) === statusId,
      );
      const row = select.closest("tr");
      if (row) {
        row.dataset.statusColor = selectedStatus?.color || "";
        applyRowColor(row, selectedStatus?.color || "");
      }

      const rowIndex = currentRows.findIndex(
        (entry) => Number(entry.id) === recordId,
      );
      if (rowIndex >= 0) {
        currentRows[rowIndex] = {
          ...currentRows[rowIndex],
          status_id: statusId,
          status_name:
            selectedStatus?.name || getRowStatusName(currentRows[rowIndex]),
          status_color: selectedStatus?.color || "",
        };
        renderStatusFilters();
        await reloadRowsSilently();
      }

      window.showSnackbar?.("Estado actualizado correctamente.", "success");
    } catch (error) {
      window.showSnackbar?.(
        error.message || "No se pudo actualizar el estado.",
        "error",
      );
    }
  }

  table?.addEventListener("change", (event) => {
    const select = event.target.closest(".carrier-tracking-status-select");
    if (!select) return;
    updateStatus(select);
  });

  closeBtn?.addEventListener("click", () => closeModal(modal));

  exportBtn?.addEventListener("click", async () => {
    await exportCurrentRows();
  });

  statusCloseBtn?.addEventListener("click", () => closeModal(statusModal));

  modal?.addEventListener("click", (event) => {
    if (event.target === modal) {
      closeModal(modal);
    }
  });

  statusModal?.addEventListener("click", (event) => {
    if (event.target === statusModal) {
      closeModal(statusModal);
    }
  });

  presetsContainer?.addEventListener("click", async (event) => {
    const button = event.target.closest("[data-range-preset]");
    if (!button) return;
    applyPreset(button.dataset.rangePreset || "current-year");

    if (!modal?.classList.contains("hidden")) {
      await reloadRowsSilently();
    }
  });

  statusFiltersContainer?.addEventListener("click", async (event) => {
    const button = event.target.closest("[data-status-filter]");
    if (!button) return;

    setActiveStatusFilter(button.dataset.statusFilter || "all");

    if (!modal?.classList.contains("hidden")) {
      await reloadRowsSilently();
    }
  });

  openStatusBtn?.addEventListener("click", async () => {
    if (!requireAuth()) return;
    currentCarrier = "CANPAR";
    openModal(statusModal);
    try {
      await loadStatusManager();
    } catch (error) {
      window.showSnackbar?.(
        error.message || "No se pudieron cargar los status.",
        "error",
      );
    }
  });

  statusForm?.addEventListener("submit", async (event) => {
    event.preventDefault();

    try {
      await requestJson(`${API_URL}/carrier-tracking/statuses`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          carrier: currentCarrier,
          name: statusForm.elements.name.value,
          color: statusForm.elements.color.value,
          sortOrder: Number(statusForm.elements.sortOrder.value || 0),
        }),
      });

      statusForm.elements.name.value = "";
      statusForm.elements.color.value = "#e5e7eb";
      statusForm.elements.sortOrder.value = "0";
      await loadStatusManager();
      if (!modal?.classList.contains("hidden")) {
        await reloadRowsSilently();
      }
      window.showSnackbar?.("Status creado correctamente.", "success");
    } catch (error) {
      window.showSnackbar?.(
        error.message || "No se pudo crear el status.",
        "error",
      );
    }
  });

  statusList?.addEventListener("click", async (event) => {
    const button = event.target.closest('[data-action="save-status"]');
    if (!button) return;

    const row = button.closest("[data-status-id]");
    if (!row) return;

    try {
      await requestJson(
        `${API_URL}/carrier-tracking/statuses/${row.dataset.statusId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            carrier: currentCarrier,
            name: row.querySelector('[data-field="name"]').value,
            color: row.querySelector('[data-field="color"]').value,
            sortOrder: Number(
              row.querySelector('[data-field="sortOrder"]').value || 0,
            ),
            isActive:
              row.querySelector('[data-field="isActive"]').disabled ||
              row.querySelector('[data-field="isActive"]').checked,
          }),
        },
      );

      await loadStatusManager();
      if (!modal?.classList.contains("hidden")) {
        await reloadRowsSilently();
      }
      window.showSnackbar?.("Status actualizado correctamente.", "success");
    } catch (error) {
      window.showSnackbar?.(
        error.message || "No se pudo actualizar el status.",
        "error",
      );
    }
  });

  window.carrierTrackingPopup = {
    async open({ carrier, title } = {}) {
      if (!requireAuth()) return;

      currentCarrier = String(
        carrier || currentCarrier || "CANPAR",
      ).toUpperCase();
      if (modalTitle) {
        modalTitle.textContent = title || `Tracking ${currentCarrier}`;
      }

      if (!currentRangePreset || currentRangePreset === "custom") {
        applyPreset("current-year");
      } else {
        applyPreset(currentRangePreset);
      }

      openModal(modal);

      try {
        await loadRows();
      } catch (error) {
        window.showSnackbar?.(
          error.message || "No se pudieron cargar los registros.",
          "error",
        );
      }
    },
    async refresh() {
      await loadRows();
    },
  };
});
